import { useState, useCallback } from "react";
import operatorsApi from "../API/operatorsApi";

export const useOperatorsApi = (showNotificationArg) => {
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [operator, setOperator] = useState(null);

  // Use passed showNotification or fallback to local (for backward compatibility)
  let showNotification = showNotificationArg;
  if (!showNotification) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    showNotification = require("./useNotification").default().showNotification;
  }

  // Get all operators
  const fetchOperators = useCallback(
    async (isActive = true) => {
      setLoading(true);
      try {
        const response = await operatorsApi.getAll(isActive);
        if (response.error) {
          showNotification(response.error, "error");
          setOperators([]);
          return { success: false, error: response.error };
        }
        setOperators(response.data || response || []);
        return { success: true, data: response.data || response };
      } catch (error) {
        console.error("Error fetching operators:", error);
        showNotification("Error fetching operators", "error");
        return {
          success: false,
          error: error.response?.data?.message || "Failed to fetch operators",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Get operator by ID
  const fetchOperatorById = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await operatorsApi.getById(id);
        setOperator(response.data);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching operator details:", error);
        showNotification("Error fetching operator details", "error");
        return {
          success: false,
          error:
            error.response?.data?.message || "Failed to fetch operator details",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Create new operator
  const createOperator = useCallback(
    async (operatorData) => {
      setLoading(true);
      try {
        const response = await operatorsApi.create(operatorData);
        showNotification("Operator created successfully", "success");
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error creating operator:", error);
        showNotification(
          error.response?.data?.message || "Failed to create operator",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to create operator",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update operator details
  const updateOperator = useCallback(
    async (id, operatorData) => {
      setLoading(true);
      try {
        await operatorsApi.update(id, operatorData);
        showNotification("Operator details updated successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error updating operator details:", error);
        showNotification(
          error.response?.data?.message || "Failed to update operator details",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message ||
            "Failed to update operator details",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Delete operator
  const deleteOperator = useCallback(
    async (id) => {
      setLoading(true);
      try {
        await operatorsApi.delete(id);
        showNotification("Operator deleted successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error deleting operator:", error);
        showNotification(
          error.response?.data?.message || "Failed to delete operator",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to delete operator",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update operator clients
  const updateOperatorClients = useCallback(
    async (id, clientIds) => {
      setLoading(true);
      try {
        await operatorsApi.updateClients(id, clientIds);
        showNotification("Operator clients updated successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error updating operator clients:", error);
        showNotification(
          error.response?.data?.message || "Failed to update operator clients",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message ||
            "Failed to update operator clients",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Get operators by client
  const fetchOperatorsByClient = useCallback(
    async (clientId) => {
      setLoading(true);
      try {
        const response = await operatorsApi.getByClient(clientId);
        setOperators(response.data || []);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching operators by client:", error);
        showNotification(
          error.response?.data?.message ||
            "Failed to fetch operators by client",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message ||
            "Failed to fetch operators by client",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update payout amount
  const updateOperatorPayout = useCallback(
    async (id, payoutAmount) => {
      setLoading(true);
      try {
        await operatorsApi.updatePayout(id, payoutAmount);
        showNotification("Operator payout updated successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error updating operator payout:", error);
        showNotification(
          error.response?.data?.message || "Failed to update operator payout",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message || "Failed to update operator payout",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Generate QR code for 2FA
  const generateOperatorQRCode = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await operatorsApi.generateQRCode(id);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error generating operator QR code:", error);
        showNotification(
          error.response?.data?.message || "Failed to generate QR code",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to generate QR code",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  return {
    loading,
    operators,
    operator,
    fetchOperators,
    fetchOperatorById,
    createOperator,
    updateOperator,
    deleteOperator,
    updateOperatorClients,
    fetchOperatorsByClient,
    updateOperatorPayout,
    generateOperatorQRCode,
  };
};

export default useOperatorsApi;
